import React, { useEffect, useRef, useState } from 'react';
import {
  getPainterlyModel,
  getPainterlyStage,
  getPainterlyProgressState,
  paintPaper,
  drawPainterlyStrokeRange,
  drawPainterlyPartialStroke,
} from '../../utils/painterlyRenderer';

// Session progress is the only clock. Pausing also pauses every paint mark.
export default function ImagePaintingCanvas({
  id,
  scene,
  progress,
  artSeed,
  className,
}) {
  const canvasRef = useRef(null);
  const surfaceRef = useRef(null);
  const [size, setSize] = useState(1);
  const [retryKey, setRetryKey] = useState(0);
  const [loadState, setLoadState] = useState('loading');
  const [loaded, setLoaded] = useState(null);
  const value = Math.max(0, Math.min(1, Number(progress) || 0));
  const model = loaded?.source === scene.source
    && loaded?.artSeed === artSeed
    && loaded?.retryKey === retryKey
    ? loaded.model : null;
  const stage = getPainterlyStage(value);
  const painted = model ? getPainterlyProgressState(model, value) : null;

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setLoaded(null);
    getPainterlyModel(scene.source, artSeed)
      .then((nextModel) => {
        if (cancelled) return;
        setLoaded({ model: nextModel, source: scene.source, artSeed, retryKey });
        setLoadState('loaded');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });
    return () => { cancelled = true; };
  }, [scene.source, artSeed, retryKey]);

  // Resize subscriptions stay stable while progress advances.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let frame = null;
    const measure = () => {
      const bounds = canvas.getBoundingClientRect();
      const cssSize = Math.max(1, Math.min(bounds.width, bounds.height));
      const next = Math.max(1, Math.min(1024,
        Math.round(cssSize * Math.min(window.devicePixelRatio || 1, 2))));
      setSize((previous) => previous === next ? previous : next);
    };
    const scheduleMeasure = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = null; measure(); });
    };
    measure();
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(scheduleMeasure) : null;
    observer?.observe(canvas);
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.width !== size || canvas.height !== size) {
      canvas.width = size;
      canvas.height = size;
    }
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return;
    if (!model) {
      surfaceRef.current = null;
      paintPaper(context, size, size, artSeed);
      return;
    }
    const state = getPainterlyProgressState(model, value);
    let surface = surfaceRef.current;
    // Replaying, changing artwork, or resizing starts from clean paper.
    if (!surface || surface.model !== model || surface.size !== size
      || state.completeCount < surface.completeCount) {
      const buffer = document.createElement('canvas');
      buffer.width = size;
      buffer.height = size;
      const bufferContext = buffer.getContext('2d', { alpha: false });
      if (!bufferContext) return;
      paintPaper(bufferContext, size, size, model.artSeed);
      surface = { model, size, buffer, context: bufferContext, completeCount: 0 };
      surfaceRef.current = surface;
    }
    // Only completed strokes are committed. The moving brush is redrawn separately.
    if (state.completeCount > surface.completeCount) {
      drawPainterlyStrokeRange(surface.context, model,
        surface.completeCount, state.completeCount, size, size);
      surface.completeCount = state.completeCount;
    }
    context.drawImage(surface.buffer, 0, 0);
    drawPainterlyPartialStroke(context, model, state.currentStroke,
      state.partial, size, size);
  }, [model, value, size, artSeed]);

  return (
    <div className={'relative overflow-hidden bg-paper ' + (className || '')}>
      <canvas
        ref={canvasRef}
        id={id}
        width={size}
        height={size}
        className="h-full w-full object-contain"
        role="img"
        aria-label={scene.alt || scene.title || 'Focus painting'}
        data-progress={value.toFixed(4)}
        data-stage={stage}
        data-stroke-count={model?.strokes.length || 0}
        data-painted-strokes={painted?.completeCount || 0}
      />
      {value > 0 && loadState === 'loading' && (
        <div role="status" className="absolute inset-0 flex items-center justify-center bg-paper/90 px-6 text-center text-sm font-medium text-pine">
          Loading artwork…
        </div>
      )}
      {value > 0 && loadState === 'error' && (
        <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-paper px-6 text-center text-sm text-pine">
          <span>This artwork could not be loaded.</span>
          <button type="button" onClick={() => setRetryKey((current) => current + 1)}
            className="min-h-11 rounded-xl border border-pine/20 bg-white px-4 py-2 font-bold focus:outline-none focus:ring-2 focus:ring-pine">
            Retry image
          </button>
          <a href={scene.source} target="_blank" rel="noreferrer" className="font-bold underline underline-offset-4">
            Open original image
          </a>
        </div>
      )}
    </div>
  );
}